import { useContext, useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import TableContext from "context/table/tableContext";
import {
    API_IS_BEING_CANCELLED,
    ENDPOINTS,
    HierarchyLoader,
    HierarchyTitleLoader,
    useApi,
    useIsMounted
} from "shared";

import { CompanyComponentProps } from "../types/interfaces";
import Company from "./Company";

export const CompanyHierarchy = ({ titleCSS }: CompanyComponentProps) => {
    const { detailsData } = useContext(TableContext);

    const { t } = useTranslation();

    const { cancelSource, isCanceled, getData, handleResponse } = useApi();
    const isMounted = useIsMounted();
    const isInitialLoad = useRef(true);

    const [isLoading, setIsLoading] = useState(false);
    const [companies, setCompanies] = useState([]);

    const { id } = detailsData;

    useEffect(() => {
        const getInheritanceData = async () => {
            try {
                setIsLoading(true);

                const { data } = await getData(
                    `${ENDPOINTS.Companies}/${id}/inheritance`
                );

                if (isMounted) {
                    setCompanies(data);
                    setIsLoading(false);
                }
            } catch (error) {
                if (isMounted && !isCanceled(error)) {
                    handleResponse(error);
                    setCompanies([]);
                    setIsLoading(false);
                }
            }

            if (isInitialLoad.current) {
                isInitialLoad.current = false;
            }
        };

        getInheritanceData();

        return () => {
            cancelSource.cancel(API_IS_BEING_CANCELLED);
        };

        // eslint-disable-next-line
    }, [id]);

    return (
        <div>
            {isInitialLoad.current ? (
                <HierarchyTitleLoader />
            ) : (
                <div
                    css={css({
                        marginBottom: "8px",
                        opacity: isLoading ? "0.4" : "1",
                        ...titleCSS
                    })}
                >
                    {t("Table##company hierarchy")}
                </div>
            )}

            {isInitialLoad.current ? (
                <HierarchyLoader />
            ) : (
                <ul
                    css={css({
                        opacity: isLoading ? "0.4" : "1",
                        display: "flex",
                        flexDirection: "column",
                        rowGap: "4px",
                        paddingBottom: "8px",
                        overflowX: "auto"
                    })}
                >
                    {companies.map((company: any, i: number) => {
                        return (
                            <Company
                                key={company.id}
                                data={company}
                                index={i}
                                isActive={i === companies.length - 1}
                            />
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default CompanyHierarchy;
